import React, { useRef, useCallback } from 'react';
import classNames from 'classnames';
import { useTheme } from '@/context/theme.context';
import {
  APP_GRADIENT,
  APP_RIPPLE,
  APP_SEMANTIC,
} from '@/constant/theme/colors.constant';
import SpinnerAtom from './Spinner';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ButtonVariant =
  | 'primary' // brand gradient — main CTA
  | 'secondary' // tinted outline — secondary action
  | 'ghost' // no bg — subtle tertiary
  | 'danger' // red — destructive
  | 'success' // green — confirm/activate
  | 'warning' // amber — caution
  | 'info' // indigo — informational / view
  | 'purple' // violet/purple — barcodes / secondary action
  | 'neutral' // gray — cancel / reset
  | 'unstyled'; // zero variant styles — className controls everything

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type ButtonShape = 'default' | 'pill' | 'square' | 'circle';

export interface AppButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style of the button */
  variant?: ButtonVariant;
  /** Height / padding / text scale */
  size?: ButtonSize;
  /** Border-radius style */
  shape?: ButtonShape;
  /** Spinner + disabled while true */
  loading?: boolean;
  /** Leading icon node */
  icon?: React.ReactNode;
  /** Trailing icon node */
  trailingIcon?: React.ReactNode;
  /** Stretch to 100% of parent width */
  fullWidth?: boolean;
  /** Ink-ripple on click (default true) */
  ripple?: boolean;
  /** Show hover background tint (default true). Set false for borderless icon circles. */
  hoverBg?: boolean;
  /** Render as light-tinted version of the variant (colored bg + border + text, no fill gradient) */
  tint?: boolean;
  /** Elevation / drop-shadow level 0-3 */
  elevation?: 0 | 1 | 2 | 3;
  /** Override flex justify alignment — default is 'center' */
  justify?: 'start' | 'center' | 'end' | 'between';
  className?: string;
  style?: React.CSSProperties;
  dataTestId?: string;
}

// ─── Static maps ─────────────────────────────────────────────────────────────

const sizeMap: Record<ButtonSize, string> = {
  xs: 'h-7  px-3   text-[10px] font-semibold tracking-wide  gap-1',
  sm: 'h-8  px-3.5 text-[11px] font-semibold tracking-wide  gap-1',
  md: 'h-9  px-4   text-[12px] font-bold     tracking-wider gap-1.5',
  lg: 'h-10 px-5   text-[13px] font-bold     tracking-wider gap-1.5',
  xl: 'h-11 px-6   text-[14px] font-bold     tracking-wider gap-2',
};

const squareSizeMap: Record<ButtonSize, string> = {
  xs: 'h-7  w-7',
  sm: 'h-8  w-8',
  md: 'h-9  w-9',
  lg: 'h-10 w-10',
  xl: 'h-11 w-11',
};

const iconSizeMap: Record<ButtonSize, string> = {
  xs: 'w-3.5 h-3.5',
  sm: 'w-4   h-4',
  md: 'w-[18px] h-[18px]',
  lg: 'w-5   h-5',
  xl: 'w-5   h-5',
};

const spinnerSizeMap: Record<ButtonSize, string> = {
  xs: 'w-3.5 h-3.5',
  sm: 'w-4   h-4',
  md: 'w-4   h-4',
  lg: 'w-5   h-5',
  xl: 'w-5   h-5',
};

const shapeMap: Record<ButtonShape, string> = {
  default: 'rounded-lg',
  pill: 'rounded-full',
  square: 'rounded-lg',
  circle: 'rounded-full',
};

const elevationMap: Record<0 | 1 | 2 | 3, string> = {
  0: 'shadow-none',
  1: 'shadow-sm',
  2: 'shadow-md',
  3: 'shadow-lg',
};

// ─── Spinner ──────────────────────────────────────────────────────────────────

const Spinner = ({ size }: { size: ButtonSize }) => (
  <SpinnerAtom
    color="current"
    className={classNames('shrink-0 flex-none', spinnerSizeMap[size])}
  />
);

// ─── Ripple ───────────────────────────────────────────────────────────────────

function spawnRipple(
  btn: HTMLButtonElement,
  e: React.MouseEvent<HTMLButtonElement>,
  color: string,
) {
  const rect = btn.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height) * 2;
  const x = e.clientX - rect.left - size / 2;
  const y = e.clientY - rect.top - size / 2;

  const ripple = document.createElement('span');
  Object.assign(ripple.style, {
    position: 'absolute',
    width: `${size}px`,
    height: `${size}px`,
    top: `${y}px`,
    left: `${x}px`,
    borderRadius: '50%',
    background: color,
    transform: 'scale(0)',
    animation: 'btn-ripple 400ms cubic-bezier(0.4,0,0.2,1) forwards',

    pointerEvents: 'none',
  });

  // Inject keyframes once
  if (!document.getElementById('btn-ripple-style')) {
    const style = document.createElement('style');
    style.id = 'btn-ripple-style';
    style.textContent = `
            @keyframes btn-ripple {
                to { transform: scale(1); opacity: 0; }
            }
        `;
    document.head.appendChild(style);
  }

  btn.appendChild(ripple);
  setTimeout(() => ripple.remove(), 450);
}

// ─── Component ────────────────────────────────────────────────────────────────

export const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      variant = 'primary',
      size = 'lg',
      shape = 'default',
      loading = false,
      icon,
      trailingIcon,
      fullWidth = false,
      ripple = true,
      hoverBg = true,
      tint = false,
      elevation,
      justify = 'center',
      children,
      className,
      style,
      disabled,
      type = 'button',
      onClick,
      dataTestId,
      ...rest
    },
    ref,
  ) => {
    const { colors } = useTheme();
    const btnRef = useRef<HTMLButtonElement>(null);
    const isSquare = shape === 'square' || shape === 'circle';
    const isDisabled = disabled || loading;

    // ── Resolve elevation default per variant ─────────────────────────────────
    const elev: 0 | 1 | 2 | 3 =
      elevation ??
      (tint
        ? 1
        : variant === 'primary' ||
            variant === 'danger' ||
            variant === 'success' ||
            variant === 'warning'
          ? 2
          : variant === 'secondary'
            ? 1
            : 0);

    // ── Per-variant inline style + tailwind class ─────────────────────────────
    let variantStyle: React.CSSProperties = {};
    let variantClass = '';
    let rippleColor: string = APP_RIPPLE.white;

    switch (variant) {
      case 'primary':
        if (tint) {
          variantStyle = {};
          variantClass = [
            APP_SEMANTIC.blue.bg,
            APP_SEMANTIC.blue.text,
            APP_SEMANTIC.blue.border,
            hoverBg ? APP_SEMANTIC.blue.hover : '',
            'active:scale-[0.98]',
          ].join(' ');
          rippleColor = APP_RIPPLE.blue;
          break;
        }
        variantStyle = {
          background: APP_GRADIENT.brandVar,
          color: 'white',
        };
        variantClass = [
          'border-0',
          'hover:brightness-110 hover:ring-2 hover:ring-inset hover:ring-white/20',
          'active:brightness-95 active:scale-[0.98]',
        ].join(' ');
        rippleColor = APP_RIPPLE.white;
        break;

      case 'secondary':
        variantStyle = {
          background: `${colors.primary}14`,
          border: `1.5px solid ${colors.primary}50`,
          color: colors.primary,
        };
        variantClass = [
          'hover:brightness-[0.93] dark:hover:brightness-110 hover:ring-1 hover:ring-inset hover:ring-black/10 dark:hover:ring-white/10',
          'active:scale-[0.98]',
        ].join(' ');
        rippleColor = `${colors.primary}28`;
        break;

      case 'ghost':
        variantStyle = { color: colors.primary };
        variantClass = [
          'bg-transparent border-0',
          hoverBg ? 'hover:bg-black/[0.05] dark:hover:bg-white/[0.08]' : '',
          hoverBg
            ? 'active:bg-black/[0.10] dark:active:bg-white/[0.14] active:scale-[0.98]'
            : 'active:scale-[0.98]',
        ].join(' ');
        rippleColor = APP_RIPPLE.primary(colors.primary);
        break;

      case 'danger':
        if (tint) {
          variantStyle = {};
          variantClass = [
            APP_SEMANTIC.danger.bg,
            APP_SEMANTIC.danger.text,
            APP_SEMANTIC.danger.border,
            hoverBg ? APP_SEMANTIC.danger.hover : '',
            'active:scale-[0.98]',
          ].join(' ');
          rippleColor = APP_RIPPLE.danger;
          break;
        }
        variantStyle = {
          background: APP_GRADIENT.danger,
          color: 'white',
        };
        variantClass = [
          'border-0',
          'hover:brightness-110 hover:ring-2 hover:ring-inset hover:ring-white/20',
          'active:brightness-95 active:scale-[0.98]',
        ].join(' ');
        rippleColor = APP_RIPPLE.white;
        break;

      case 'success':
        if (tint) {
          variantStyle = {};
          variantClass = [
            APP_SEMANTIC.success.bg,
            APP_SEMANTIC.success.text,
            APP_SEMANTIC.success.border,
            hoverBg ? APP_SEMANTIC.success.hover : '',
            'active:scale-[0.98]',
          ].join(' ');
          rippleColor = APP_RIPPLE.success;
          break;
        }
        variantStyle = {
          background: APP_GRADIENT.success,
          color: 'white',
        };
        variantClass = [
          'border-0',
          'hover:brightness-110 hover:ring-2 hover:ring-inset hover:ring-white/20',
          'active:brightness-95 active:scale-[0.98]',
        ].join(' ');
        rippleColor = APP_RIPPLE.white;
        break;

      case 'warning':
        if (tint) {
          variantStyle = {};
          variantClass = [
            APP_SEMANTIC.warning.bg,
            APP_SEMANTIC.warning.text,
            APP_SEMANTIC.warning.border,
            hoverBg ? APP_SEMANTIC.warning.hover : '',
            'active:scale-[0.98]',
          ].join(' ');
          rippleColor = APP_RIPPLE.warning;
          break;
        }
        variantStyle = {
          background: APP_GRADIENT.warning,
          color: 'white',
        };
        variantClass = [
          'border-0',
          'hover:brightness-110 hover:ring-2 hover:ring-inset hover:ring-white/20',
          'active:brightness-95 active:scale-[0.98]',
        ].join(' ');
        rippleColor = APP_RIPPLE.white;
        break;

      case 'info':
        variantStyle = {};
        variantClass = [
          APP_SEMANTIC.info.bg,
          APP_SEMANTIC.info.text,
          APP_SEMANTIC.info.border,
          hoverBg ? APP_SEMANTIC.info.hover : '',
          'active:scale-[0.98]',
        ].join(' ');
        rippleColor = APP_RIPPLE.info;
        break;

      case 'purple':
        if (tint) {
          variantStyle = {};
          variantClass = [
            APP_SEMANTIC.purple.bg,
            APP_SEMANTIC.purple.text,
            APP_SEMANTIC.purple.border,
            hoverBg ? APP_SEMANTIC.purple.hover : '',
            'active:scale-[0.98]',
          ].join(' ');
          rippleColor = APP_RIPPLE.purple;
          break;
        }
        variantStyle = {
          background: APP_GRADIENT.purple,
          color: 'white',
        };
        variantClass = [
          'border-0',
          'hover:brightness-110 hover:ring-2 hover:ring-inset hover:ring-white/20',
          'active:brightness-95 active:scale-[0.98]',
        ].join(' ');
        rippleColor = APP_RIPPLE.white;
        break;

      case 'neutral':
        variantStyle = {};
        variantClass = [
          APP_SEMANTIC.neutral.bg,
          APP_SEMANTIC.neutral.text,
          APP_SEMANTIC.neutral.border,
          hoverBg
            ? `${APP_SEMANTIC.neutral.hover} hover:ring-1 hover:ring-inset hover:ring-black/10 dark:hover:ring-white/10`
            : '',
          hoverBg
            ? 'active:bg-gray-300/60 dark:active:bg-white/[0.18] active:scale-[0.98]'
            : 'active:scale-[0.98]',
        ].join(' ');
        rippleColor = APP_RIPPLE.neutral;
        break;

      case 'unstyled':
        variantStyle = {};
        variantClass = 'active:scale-[0.98]';
        rippleColor = APP_RIPPLE.dark;
        break;
    }

    // ── Icon cloner ───────────────────────────────────────────────────────────
    const cloneIcon = (node: React.ReactNode) => {
      if (!node || !React.isValidElement(node)) return node;
      return React.cloneElement(node as React.ReactElement, {
        className: classNames(
          iconSizeMap[size],
          'shrink-0 flex-none',
          (node as React.ReactElement).props?.className,
        ),
      });
    };

    // ── Ripple handler ────────────────────────────────────────────────────────
    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        const btn =
          (ref as React.RefObject<HTMLButtonElement>)?.current ??
          btnRef.current;
        if (ripple && btn && !isDisabled) spawnRipple(btn, e, rippleColor);
        onClick?.(e);
      },
      [ripple, isDisabled, rippleColor, onClick, ref],
    );

    return (
      <button
        ref={(node) => {
          (btnRef as React.MutableRefObject<HTMLButtonElement | null>).current =
            node;
          if (typeof ref === 'function') ref(node);
          else if (ref)
            (ref as React.MutableRefObject<HTMLButtonElement | null>).current =
              node;
        }}
        type={type}
        disabled={isDisabled}
        onClick={handleClick}
        style={{ outline: 'none', ...variantStyle, ...style }}
        data-testid={dataTestId}
        className={classNames(
          // ── base ──────────────────────────────────────────────────────
          'relative overflow-hidden inline-flex items-center border',
          justify === 'start'
            ? 'justify-start'
            : justify === 'end'
              ? 'justify-end'
              : justify === 'between'
                ? 'justify-between'
                : 'justify-center',
          'max-w-full min-w-0 shrink-0 select-none cursor-pointer whitespace-nowrap',
          'transition-all duration-200 will-change-transform',
          'outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-current/30',
          // ── disabled ──────────────────────────────────────────────────
          'disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100 disabled:shadow-none',
          // ── layout ────────────────────────────────────────────────────
          fullWidth && 'w-full',
          // ── shape + size ──────────────────────────────────────────────
          shapeMap[shape],
          isSquare ? squareSizeMap[size] : sizeMap[size],
          // ── elevation ─────────────────────────────────────────────────
          elevationMap[elev],
          // ── variant ───────────────────────────────────────────────────
          variantClass,
          // ── user overrides ────────────────────────────────────────────
          className,
        )}
        {...rest}
      >
        {loading ? (
          <>
            <Spinner size={size} />
            {!isSquare && (
              <span className="ml-1 min-w-0 truncate opacity-80">Loading…</span>
            )}
          </>
        ) : (
          <>
            {icon && cloneIcon(icon)}
            {!isSquare && <span className="min-w-0 truncate">{children}</span>}
            {trailingIcon && cloneIcon(trailingIcon)}
          </>
        )}
      </button>
    );
  },
);

AppButton.displayName = 'AppButton';
export default AppButton;
